<template>
  <div v-if="isOpen" class="settings-overlay" @click.self="close">
    <div class="modal" :class="{ wide: mode === 'diagnostics' }" role="dialog" aria-modal="true">

      <header class="modal-head">
        <div class="head-grp">
          <h3>Settings</h3>
          <div class="seg" role="tablist" aria-label="Settings mode">
            <button type="button" role="tab" :aria-selected="mode === 'settings'" :class="{ on: mode === 'settings' }" @click="mode = 'settings'">Settings</button>
            <button type="button" role="tab" :aria-selected="mode === 'diagnostics'" :class="{ on: mode === 'diagnostics' }" @click="mode = 'diagnostics'">Diagnostics</button>
          </div>
        </div>
        <button class="btn btn-ghost" type="button" @click="close">Close</button>
      </header>

      <!-- ───────────────────────── SETTINGS (everyday) ───────────────────────── -->
      <template v-if="mode === 'settings'">
        <div class="panes">
          <div class="pane">
            <div class="grp">
              <h4>Status</h4>
              <div class="kv"><span class="k">Connection</span><span class="v" :class="{ ok: connHealth === 'ok' }">{{ connectionLabel }}</span></div>
              <div class="kv"><span class="k">Uptime</span><span class="v">{{ connectedDuration }}</span></div>
              <div class="kv"><span class="k">Auth</span><span class="v">{{ authLabel }}</span></div>
              <div class="kv"><span class="k">Device</span><span class="v">{{ deviceId }}</span></div>
            </div>
            <div class="grp">
              <h4>Layout</h4>
              <div class="kv"><span class="k">Current</span><span class="v">{{ layoutDisplay }}</span></div>
              <div class="kv"><span class="k">Loaded</span><span class="v">{{ layoutLoaded }}</span></div>
              <div class="kv"><span class="k">Elements</span><span class="v">{{ elementCount.toLocaleString() }}</span></div>
            </div>
            <div class="grp">
              <h4>Environment</h4>
              <div class="kv"><span class="k">Socket</span><span class="v v-trunc" :title="socketURL">{{ socketURL }}</span></div>
              <div class="kv"><span class="k">Client</span><span class="v">v{{ clientVersion }}</span></div>
            </div>
            <div class="grp">
              <h4>Subscriptions</h4>
              <div class="kv"><span class="k">Rules</span><span class="v">{{ ruleCount }}</span></div>
              <div class="kv"><span class="k">Channels</span><span class="v">{{ channelCount }}</span></div>
            </div>
          </div>

          <div class="pane">
            <div class="acts">
              <p class="lbl">Actions</p>
              <button class="btn btn-outline" type="button" @click="forceReconnect">
                <svg class="btn-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h.01"/><path d="M2 8.82a15 15 0 0 1 20 0"/><path d="M5 12.859a10 10 0 0 1 14 0"/><path d="M8.5 16.429a5 5 0 0 1 7 0"/></svg>
                Reconnect
              </button>
              <button class="btn btn-outline" type="button" @click="refreshLayout">
                <svg class="btn-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="7" height="9" x="3" y="3" rx="1"/><rect width="7" height="5" x="14" y="3" rx="1"/><rect width="7" height="9" x="14" y="12" rx="1"/><rect width="7" height="5" x="3" y="16" rx="1"/></svg>
                Refresh layout
              </button>
              <button class="btn btn-outline" type="button" @click="reloadApp">
                <svg class="btn-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/></svg>
                Reload app
              </button>
              <button class="btn btn-outline" type="button" @click="toggleCache">
                <svg class="btn-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5V19A9 3 0 0 0 21 19V5"/><path d="M3 12A9 3 0 0 0 21 12"/></svg>
                {{ cacheEnabled ? "Disable cache" : "Enable cache" }}
              </button>
              <div class="sep"></div>
              <button class="btn btn-danger" type="button" @click="clearCache">
                <svg class="btn-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/><line x1="10" x2="10" y1="11" y2="17"/><line x1="14" x2="14" y1="11" y2="17"/></svg>
                Clear cache
              </button>
              <button v-if="rebootAvailable" class="btn btn-danger" type="button" @click="rebootDevice">
                <svg class="btn-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2v10"/><path d="M18.4 6.6a9 9 0 1 1-12.77.04"/></svg>
                Reboot device
              </button>
              <button class="btn btn-danger" type="button" @click="signOut">
                <svg class="btn-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" x2="9" y1="12" y2="12"/></svg>
                Sign out
              </button>
              <div v-if="actionMessage" class="hint">{{ actionMessage }}</div>
            </div>
          </div>
        </div>
      </template>

      <!-- ───────────────────────── DIAGNOSTICS (for support) ───────────────────────── -->
      <template v-else>
        <div class="diag-health">
          <span class="hchip"><span class="d" :class="connHealth"></span><b>Connection</b></span>
          <span class="hchip"><span class="d" :class="cacheActive ? 'ok' : 'warn'"></span><b>Cache</b></span>
          <span class="hchip"><span class="d" :class="store.loaded ? 'ok' : 'warn'"></span><b>Layout</b></span>
          <span class="hchip"><span class="d" :class="unavailableRuleCount === 0 ? 'ok' : 'warn'"></span><b>Rules</b></span>
        </div>

        <div class="diag-body">
          <div class="diag-sec">
            <h4>Connection &amp; session</h4>
            <div class="diag-kv">
              <div class="kv"><span class="k">Auth</span><span class="v">{{ authLabel }}</span></div>
              <div class="kv"><span class="k">Pending (socket · ui)</span><span class="v">{{ pendingUpdates }} · {{ pendingUiOps }}</span></div>
              <div class="kv"><span class="k">Disconnected</span><span class="v">{{ disconnectedDuration }}</span></div>
              <div class="kv"><span class="k">Device</span><span class="v">{{ deviceId }}</span></div>
            </div>
          </div>

          <div class="diag-sec">
            <h4>Cache</h4>
            <div class="diag-kv">
              <div class="kv"><span class="k">Layout cached</span><span class="v">{{ layoutCacheStatus }}</span></div>
              <div class="kv"><span class="k">Clone hash</span><span class="v v-trunc" :title="cloneHash">{{ cloneHash }}</span></div>
            </div>
          </div>

          <div class="diag-sec">
            <h4>Environment</h4>
            <div class="diag-kv">
              <div class="kv"><span class="k">Mode</span><span class="v">{{ envMode }}</span></div>
              <div class="kv"><span class="k">OS</span><span class="v">{{ operatingSystem }}</span></div>
              <div class="kv"><span class="k">Origin</span><span class="v v-trunc" :title="origin">{{ origin }}</span></div>
              <div class="kv"><span class="k">Socket</span><span class="v v-trunc" :title="socketURL">{{ socketURL }}</span></div>
              <div class="kv"><span class="k">Content URL</span><span class="v v-trunc" :title="content_url">{{ content_url }}</span></div>
              <div class="kv"><span class="k">Server · client</span><span class="v">{{ serverVersion }} · {{ clientVersion }}</span></div>
            </div>
          </div>

          <div class="diag-sec">
            <h4>Channels <span class="count">{{ channelCount }} · {{ totalRows.toLocaleString() }} rows</span></h4>
            <div class="ch-filter">
              <div class="seg sm" role="tablist" aria-label="Channel filter">
                <button type="button" role="tab" :aria-selected="channelFilter === 'issues'" :class="{ on: channelFilter === 'issues' }" @click="channelFilter = 'issues'">Issues · {{ issueChannels.length }}</button>
                <button type="button" role="tab" :aria-selected="channelFilter === 'all'" :class="{ on: channelFilter === 'all' }" @click="channelFilter = 'all'">All · {{ channelCount }}</button>
              </div>
              <span class="ch-note">Sorted by status, problems first</span>
            </div>
            <div class="ctable scroll">
              <div class="ch-row ch-head"><span class="name">Channel</span><span class="rows">Rows</span><span>Fill</span></div>
              <template v-if="visibleChannels.length > 0">
                <div v-for="c in visibleChannels" :key="c.name" class="ch-row">
                  <span class="name">{{ c.name }}</span>
                  <span class="rows">{{ c.rows }}{{ c.expected != null ? `/${c.expected}` : "" }}</span>
                  <span class="cbar"><i :class="c.status" :style="{ width: c.fill + '%' }"></i></span>
                </div>
              </template>
              <div v-else class="ch-empty">No channels subscribed.</div>
            </div>
          </div>

          <div class="diag-sec">
            <h4>Rules <span class="count">{{ ruleCount }} rule{{ ruleCount !== 1 ? "s" : "" }}<template v-if="unavailableRuleCount > 0"> · {{ unavailableRuleCount }} unavailable</template></span></h4>
            <div v-if="ruleDiags.length > 0" class="rule-list scroll">
              <span
                v-for="rule in ruleDiags"
                :key="rule.name"
                class="r"
                :class="{ off: !rule.available }"
                :title="rule.available ? rule.name : `${rule.name} — unavailable`"
              >{{ rule.name }}</span>
            </div>
            <div v-else class="ch-empty">No rules loaded.</div>
          </div>
        </div>

        <div class="diag-foot">
          <span class="ts">{{ actionMessage || `${snapshotTime} · auto-refreshing` }}</span>
          <div class="foot-grp">
            <button class="btn btn-outline" type="button" @click="downloadReport">Download report</button>
          </div>
        </div>
      </template>

    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, nextTick, ref, watch} from "vue";
import {useRouter} from "vue-router";
import useStore from "../store.ts";
import {ConnectionState} from "../socket/ConnectionState.ts";
import UpdateCoordinator from "../utils/UpdateCoordinator.ts";
import {
  isStructuredCacheEnabled,
  killStructuredCache,
  setStructuredCacheEnabled,
  canPersistSnapshot,
} from "../cache/CacheControl.ts";
import {CacheManager} from "../cache/CacheManager.ts";
import {integrationManager} from "../integrations/IntegrationManager.ts";
import {clearStoredDeviceId} from "../utils/DeviceId.ts";

const store = useStore();
const router = useRouter();
const isOpen = computed(() => store.ui.settingsOpen);

const mode = ref<"settings" | "diagnostics">("settings");
const channelFilter = ref<"issues" | "all">("issues");

const cacheEnabled = ref(false);
const cacheActive = ref(false);
const layoutCacheStatus = ref("unknown");
const actionMessage = ref("");
const rebootAvailable = ref(false);

const envMode = import.meta.env.MODE || "unknown";
const origin = window.location.origin;

const connectionLabel = computed(() => store.connection.state || (store.connected ? "connected" : "disconnected"));
const connHealth = computed<"ok" | "warn" | "err">(() => {
  const s = store.connection.state;
  if (s === ConnectionState.Ready || s === ConnectionState.Syncing) return "ok";
  if (s === ConnectionState.Disconnected) return "err";
  return "warn";
});
const authLabel = computed(() => store.socketManager.isAuthenticated() ? "authenticated" : "unauthenticated");
const deviceId = computed(() => store.socketManager.getDeviceId() ?? "unknown");
const layoutId = computed(() => store.layout != null ? String(store.layout) : "unknown");
const layoutDisplay = computed(() => store.layout != null ? `#${store.layout}` : "n/a");
const layoutLoaded = computed(() => store.loaded ? "loaded" : "loading");
const elementCount = computed(() => Object.keys(store.components).length);
const cloneHash = computed(() => store.clone.transformHash || "none");
const pendingUpdates = computed(() => store.socketManager.getQueueLength());
const pendingUiOps = computed(() => UpdateCoordinator.pendingCount.value);
const rules = computed(() => store.ruleManager.listRuleNames());
const channels = computed(() => {
  return store.channelManager.getChannels().map((channel) => {
    const rows = channel.getRows()?.length ?? 0;
    const expected = channel.getExpectedRows?.() ?? 0;
    return {
      name: channel.getName(),
      rows,
      expected: expected > 0 ? expected : null,
      pending: channel.isPending?.() ?? false,
    };
  });
});
const ruleCount = computed(() => rules.value.length);
const channelCount = computed(() => channels.value.length);

// see docs/EXTRACTED_COMMENTS.md#c353
type ChannelDiag = {
  name: string;
  rows: number;
  expected: number | null;
  fill: number;
  status: "live" | "syncing" | "stalled";
  label: string;
};

const STATUS_ORDER: Record<ChannelDiag["status"], number> = { stalled: 0, syncing: 1, live: 2 };

const channelDiags = computed<ChannelDiag[]>(() => {
  const list: ChannelDiag[] = channels.value.map((ch) => {
    const expected = ch.expected;
    const rows = ch.rows;
    let status: ChannelDiag["status"] = "live";
    // see docs/EXTRACTED_COMMENTS.md#c354
    let fill = rows === 0 ? 0 : 100;
    let label = "";
    if (ch.pending) {
      status = "syncing";
      label = "syncing";
      if (expected != null && rows < expected) {
        fill = Math.min(100, Math.round((rows / Math.max(1, expected)) * 100));
      }
    }
    return { name: ch.name, rows, expected, fill, status, label };
  });
  return list.sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || a.fill - b.fill);
});

// available = the server delivered this rule's definition
const ruleDiags = computed<{ name: string; available: boolean }[]>(() => {
  return rules.value
    .map((name) => ({
      name,
      available: (store.ruleManager.getRule(name)?.getValue()?.length ?? 0) > 0,
    }))
    .sort((a, b) => a.name.localeCompare(b.name, undefined, {numeric: true, sensitivity: "base"}));
});
const unavailableRuleCount = computed(() => ruleDiags.value.filter((r) => !r.available).length);

const issueChannels = computed(() => channelDiags.value.filter((c) => c.status !== "live"));
const visibleChannels = computed(() => channelFilter.value === "issues" ? issueChannels.value : channelDiags.value);
const totalRows = computed(() => channelDiags.value.reduce((s, c) => s + c.rows, 0));

const socketURL = computed(() => (store.socketManager.getURL() || "n/a").replace("https://", ""));
const content_url = computed(() => (store.device_settings?.content_url || "n/a").replace("https://", ""));
const operatingSystem = computed(() => {
  const ua = window.navigator.userAgent;
  let os = "Unknown OS";
  let version = "Unknown Version";

  const clientStrings = [
    { s: 'Windows 10', r: /(Windows 10.0|Windows NT 10.0)/, v: '10.0' },
    { s: 'Windows 8.1', r: /(Windows 8.1|Windows NT 6.3)/, v: '8.1' },
    { s: 'Windows 8', r: /(Windows 8|Windows NT 6.2)/, v: '8' },
    { s: 'Windows 7', r: /(Windows 7|Windows NT 6.1)/, v: '7' },
    { s: 'Android', r: /Android/, v: /Android ([\d.]+)/ },
    { s: 'iPhone', r: /(iPhone|iPad|iPod)/, v: /OS ([\d_]+)/ },
    { s: 'Mac OS X', r: /Mac OS X/, v: /Mac OS X ([\d._]+)/ },
    { s: 'Linux', r: /Linux/, v: /Linux/ }
  ];

  for (let id in clientStrings) {
    let cs = clientStrings[id];
    if (cs.r.test(ua)) {
      os = cs.s;

      // Extract version if regex provided
      if (cs.v instanceof RegExp) {
        let match = ua.match(cs.v);
        if (match && match[1]) {
          version = match[1].replace(/_/g, '.');
        }
      } else {
        version = cs.v;
      }
      break;
    }
  }

  return `${os} ${version}`;
});
const clientVersion = computed(() => integrationManager.getRuntimeVersion());

const serverVersion = computed(() => {
  return "unknown";
})

const connectedDuration = computed(() => {
  if (!store.connected || store.connection.connectedAt == null) return "n/a";
  return formatDuration(store.currentDate - store.connection.connectedAt);
});

const disconnectedDuration = computed(() => {
  if (store.connected || store.connection.lastDisconnectedAt == null) return "n/a";
  return formatDuration(store.currentDate - store.connection.lastDisconnectedAt);
});

const formatDuration = (ms: number): string => {
  if (!Number.isFinite(ms) || ms < 0) return "n/a";
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0 || days > 0) parts.push(`${hours}h`);
  if (minutes > 0 || hours > 0 || days > 0) parts.push(`${minutes}m`);
  parts.push(`${seconds}s`);
  return parts.join(" ");
};

const pad2 = (n: number): string => String(n).padStart(2, "0");
const snapshotTime = computed(() => {
  const d = new Date(store.currentDate);
  return `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}:${pad2(d.getUTCSeconds())} UTC`;
});

const refreshCacheStatus = () => {
  cacheEnabled.value = isStructuredCacheEnabled();
  cacheActive.value = canPersistSnapshot();
};

const refreshRebootAvailability = () => {
  rebootAvailable.value = integrationManager.canReboot();
};

const refreshLayoutCache = async () => {
  if (!cacheActive.value) {
    layoutCacheStatus.value = "n/a";
    return;
  }
  if (store.layout == null) {
    layoutCacheStatus.value = "n/a";
    return;
  }
  const snap = await CacheManager.instance.restoreLatestSnapshot(String(store.layout));
  layoutCacheStatus.value = snap ? "yes" : "missing";
};

const clearCache = async () => {
  actionMessage.value = "Clearing cache...";
  await killStructuredCache();
  clearStoredDeviceId();
  refreshCacheStatus();
  await refreshLayoutCache();
  actionMessage.value = "Cache cleared.";
};

const toggleCache = async () => {
  const next = !isStructuredCacheEnabled();
  setStructuredCacheEnabled(next);
  refreshCacheStatus();
  await refreshLayoutCache();
  actionMessage.value = next ? "Cache enabled." : "Cache disabled.";
};

const refreshLayout = async () => {
  actionMessage.value = "Refreshing layout...";
  await store.socketManager.requestLayout();
  actionMessage.value = "Layout refresh requested.";
};

const forceReconnect = () => {
  actionMessage.value = "Reconnecting...";
  store.socketManager.disconnect();
  setTimeout(() => store.socketManager.connect(), 500);
  actionMessage.value = "Reconnect initiated.";
};

const reloadApp = () => {
  actionMessage.value = "Reloading app...";
  window.location.reload();
};

const rebootDevice = async () => {
  actionMessage.value = "Reboot requested...";
  try {
    await integrationManager.rebootDevice();
    actionMessage.value = "Reboot requested.";
  } catch (_e) {
    actionMessage.value = "Failed to request reboot.";
  }
};


const signOut = async () => {
  store.socketManager.disconnect();
  await router.replace({path: "/auth"});
  // see docs/EXTRACTED_COMMENTS.md#c805
  await nextTick();
  await store.reset();
  store.layoutData = [];
  store.loaded = false;
  store.layout = null;
  store.device_settings = undefined;
};

// see docs/EXTRACTED_COMMENTS.md#c355
const buildReport = (): string => {
  const lines: string[] = [];
  lines.push("Lumids Display Engine diagnostics report");
  lines.push("");
  lines.push("[Connection & session]");
  lines.push(`state=${connectionLabel.value}`);
  lines.push(`uptime=${connectedDuration.value}`);
  lines.push(`disconnectedFor=${disconnectedDuration.value}`);
  lines.push(`auth=${authLabel.value}`);
  lines.push(`pending=${pendingUpdates.value} socket · ${pendingUiOps.value} ui`);
  lines.push(`device=${deviceId.value}`);
  lines.push("");
  lines.push("[Cache]");
  lines.push(`structuredCache=${cacheEnabled.value ? "enabled" : "disabled"}`);
  lines.push(`layoutCached=${layoutCacheStatus.value}`);
  lines.push(`cloneHash=${cloneHash.value}`);
  lines.push("");
  lines.push("[Layout]");
  lines.push(`current=${layoutId.value}`);
  lines.push(`loaded=${layoutLoaded.value}`);
  lines.push(`elements=${elementCount.value}`);
  lines.push("");
  lines.push("[Environment]");
  lines.push(`mode=${envMode}`);
  lines.push(`os=${operatingSystem.value}`);
  lines.push(`origin=${origin}`);
  lines.push(`socket=${socketURL.value}`);
  lines.push(`contentUrl=${content_url.value}`);
  lines.push(`serverVersion=${serverVersion.value} clientVersion=${clientVersion.value}`);
  lines.push("");
  lines.push(`[Channels] ${channelCount.value} · ${totalRows.value} rows`);
  for (const c of channelDiags.value) {
    lines.push(`  ${c.name}\t${c.rows}${c.expected != null ? "/" + c.expected : ""}\t${c.label}`);
  }
  lines.push("");
  lines.push(`[Rules] ${ruleCount.value} · ${unavailableRuleCount.value} unavailable`);
  for (const r of ruleDiags.value) lines.push(`  ${r.name}${r.available ? "" : "\tunavailable"}`);
  return lines.join("\n");
};

const downloadReport = () => {
  try {
    const blob = new Blob([buildReport()], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `lumids-diagnostics-${store.currentDate}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    actionMessage.value = "Report downloaded.";
  } catch (_e) {
    actionMessage.value = "Failed to download report.";
  }
};

const close = () => {
  store.ui.settingsOpen = false;
};

watch(
  () => store.ui.settingsOpen,
  async (open) => {
    if (!open) return;
    // see docs/EXTRACTED_COMMENTS.md#c356
    mode.value = "settings";
    channelFilter.value = "all";
    actionMessage.value = "";
    refreshRebootAvailability();
    refreshCacheStatus();
    await refreshLayoutCache();
  },
  {immediate: true}
);

watch(
  () => store.layout,
  async () => {
    if (!store.ui.settingsOpen) return;
    await refreshLayoutCache();
  }
);

// see docs/EXTRACTED_COMMENTS.md#c357
watch(
  () => issueChannels.value.length,
  (count) => {
    if (count === 0 && channelFilter.value === "issues") {
      channelFilter.value = "all";
    }
  }
);
</script>

<style scoped>
.settings-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 2, 37, 0.28);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  font-family: var(--brand);
}

.modal {
  position: relative;
  width: min(680px, 100%);
  max-height: 60vh;
  /* see docs/EXTRACTED_COMMENTS.md#c308 */
  transform: scale(1.5);
  transform-origin: center;
  background: var(--bg);
  color: var(--ink);
  border: 1px solid var(--border);
  border-radius: var(--r-lg);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal.wide {
  width: min(760px, 100%);
}

.modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;
}

.modal-head h3 {
  margin: 0;
  font-size: 1.0625rem;
  font-weight: 700;
  letter-spacing: -0.01em;
}

.head-grp {
  display: flex;
  align-items: center;
  gap: 14px;
}

/* Segmented mode toggle */
.seg {
  display: inline-flex;
  background: var(--muted);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 3px;
  gap: 3px;
}

.seg button {
  border: 0;
  background: transparent;
  font: inherit;
  font-size: 12.5px;
  font-weight: 500;
  color: var(--ink-3);
  padding: 5px 13px;
  border-radius: 6px;
  cursor: pointer;
}

.seg button.on {
  background: var(--bg);
  color: var(--ink);
  box-shadow: var(--shadow-sm);
}

.seg button:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--ring);
}

.seg.sm {
  padding: 2px;
}

.seg.sm button {
  padding: 3px 9px;
  font-size: 11.5px;
}

/* Buttons */
.btn {
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: var(--r-sm);
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 0 16px;
  border: 1px solid transparent;
  transition: background .15s var(--ease), border-color .15s var(--ease);
}

.btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--ring);
}

.btn-primary {
  background: var(--primary);
  color: #fff;
  box-shadow: var(--shadow-sm);
}

.btn-primary:hover {
  background: var(--primary-700);
}

.btn-outline {
  background: var(--bg);
  color: var(--ink);
  border-color: var(--input);
  box-shadow: var(--shadow-sm);
}

.btn-outline:hover {
  background: var(--muted);
}

.btn-ghost {
  background: transparent;
  color: var(--ink-3);
}

.btn-ghost:hover {
  color: var(--ink);
}

.btn-danger {
  background: var(--bg);
  color: var(--err);
  border-color: rgba(239, 68, 68, .3);
  box-shadow: var(--shadow-sm);
}

.btn-danger:hover {
  background: rgba(239, 68, 68, .06);
}

/* Settings panes */
.panes {
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  overflow: auto;
}

.pane {
  padding: 18px 20px;
}

.pane + .pane {
  border-left: 1px solid var(--border);
}

.grp {
  margin-bottom: 16px;
}

.grp:last-child {
  margin-bottom: 0;
}

.grp h4 {
  margin: 0 0 7px;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-4);
  font-weight: 600;
}

.kv {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  font-size: 13px;
  padding: 3px 0;
}

.kv .k {
  color: var(--ink-3);
  flex-shrink: 0;
}

.kv .v {
  font-variant-numeric: tabular-nums;
  text-align: right;
  color: var(--ink);
  min-width: 0;
}

.kv .v.ok {
  color: var(--ok);
  font-weight: 600;
}

.v-trunc {
  /* see docs/EXTRACTED_COMMENTS.md#c309 */
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.acts {
  display: grid;
  gap: 8px;
}

.acts .lbl {
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-4);
  font-weight: 600;
  margin: 0 0 2px;
}

.acts .btn {
  justify-content: flex-start;
}

.btn-ic {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.acts .sep {
  height: 1px;
  background: var(--border);
  margin: 8px 0 4px;
}

.hint {
  font-size: 12px;
  color: var(--ink-3);
  margin-top: 4px;
}

/* Diagnostics: health strip */
.diag-health {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;
}

.hchip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  padding: 5px 11px;
  border: 1px solid var(--border);
  border-radius: 999px;
  background: var(--panel);
}

.hchip .d {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--ok);
  flex-shrink: 0;
}

.hchip .d.ok { background: var(--ok); }
.hchip .d.warn { background: var(--warn); }
.hchip .d.err { background: var(--err); }

.hchip b {
  font-weight: 600;
}

/* Diagnostics: body */
.diag-body {
  overflow: auto;
  padding: 4px 20px 10px;
}

.diag-sec {
  padding: 14px 0;
  border-bottom: 1px solid var(--border);
}

.diag-sec:last-child {
  border-bottom: 0;
}

.diag-sec > h4 {
  margin: 0 0 10px;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-4);
  font-weight: 600;
  display: flex;
  justify-content: space-between;
}

.diag-sec > h4 .count {
  color: var(--ink-3);
  font-family: var(--mono);
  text-transform: none;
  letter-spacing: 0;
}

.diag-kv {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2px 28px;
}

.diag-kv .kv {
  padding: 2px 0;
}

.diag-kv .kv .v {
  font-family: var(--mono);
  font-size: 11.5px;
  max-width: 170px;
}

/* Channel table */
.ctable {
  font-size: 12.5px;
}

.ch-filter {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.ch-note {
  font-size: 11px;
  color: var(--ink-4);
}

.ctable.scroll {
  max-height: 152px;
  overflow: auto;
}

.ch-row {
  display: grid;
  grid-template-columns: 1.5fr 64px 1fr;
  gap: 14px;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid var(--border);
}

.ch-row:last-child {
  border-bottom: 0;
}

.ch-head {
  color: var(--ink-4);
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.ch-row .name {
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ch-row .rows {
  font-family: var(--mono);
  font-size: 11px;
  color: var(--ink-3);
  text-align: right;
}

.ch-head .rows {
  text-align: right;
}

.cbar {
  height: 4px;
  border-radius: 999px;
  background: var(--border);
  overflow: hidden;
}

.cbar > i {
  display: block;
  height: 100%;
  background: var(--ok);
  border-radius: 999px;
}

.cbar > i.live { background: var(--ok); }
.cbar > i.syncing { background: var(--warn); }
.cbar > i.stalled { background: var(--err); }

.ch-empty {
  font-size: 12px;
  color: var(--ink-4);
  padding: 8px 0;
}

.ch-more {
  width: 100%;
  text-align: left;
  background: none;
  border: 0;
  border-top: 1px dashed var(--border);
  margin-top: 2px;
  padding: 9px 0 2px;
  font: inherit;
  font-size: 12px;
  color: var(--primary);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 7px;
}

.ch-more:hover {
  color: var(--primary-700);
}

.ch-more .chev {
  width: 13px;
  height: 13px;
}

/* Rules */
.rule-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 0 20px;
  align-content: start;
}

.rule-list.scroll {
  max-height: 152px;
  overflow: auto;
}

.rule-list .r {
  font-family: var(--mono);
  font-size: 11.5px;
  line-height: 1.2;
  color: var(--ink-2);
  padding: 5px 0;
  border-bottom: 1px solid var(--border);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rule-list .r.off {
  color: var(--ink-4);
  text-decoration: line-through;
}

/* Diagnostics: footer */
.diag-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 13px 20px;
  border-top: 1px solid var(--border);
  background: var(--muted);
  flex-shrink: 0;
}

.diag-foot .ts {
  font-size: 12px;
  color: var(--ink-3);
  font-variant-numeric: tabular-nums;
}

.foot-grp {
  display: flex;
  gap: 8px;
}

@media (max-width: 720px) {
  .panes,
  .diag-kv {
    grid-template-columns: 1fr;
  }
  .pane + .pane {
    border-left: 0;
    border-top: 1px solid var(--border);
  }
}
</style>
